import { combineQueries } from '@datorama/akita';
import { MarketplaceItem } from './models/marketplace-item.model';
import { BalanceQuery } from '../../account/balance/balance.query';
import { map } from 'rxjs/operators';
import { config } from '../../shared/config';
import { MarketplaceItemStatus } from './models/marketplace-item-status';
import { MarketplaceQuery } from './marketplace.query';
import { Observable } from 'rxjs';
import { MarketplaceItemExtended } from './models/marketplace-item-extended.model';
import { Injectable } from '@angular/core';
import { ProcessProviderId } from './models/process-provider-id';
import { MarketplaceItemAttribute } from './models/attributes/marketplace-item-attribute.model';
import { MarketplaceItemAttributeValue } from './models/attributes/marketplace-item-attribute-value.model';
import { AttributeValue } from '../../loyalty-history/purchase/models/attribute-value';
import { DateTime } from 'luxon';

@Injectable({ providedIn: 'root' })
export class MarketplaceExtendedQuery {
  constructor(
    protected marketplaceQuery: MarketplaceQuery,
    private balanceQuery: BalanceQuery
  ) {}

  /** All marketplace items. */
  selectExtendedMarketplaceItems(): Observable<MarketplaceItemExtended[] | undefined> {
    return this._mapArray(this.marketplaceQuery.selectMarketplaceItems());
  }

  /** A specific marketplace item by ID. */
  selectExtendedMarketplaceItem(id: number): Observable<MarketplaceItemExtended | undefined> {
    return this._mapSingle(this.marketplaceQuery.selectEntity(id));
  }

  /** All marketplace items that match any of the specified types. */
  selectExtendedMarketplaceItemsOfType(...types: ProcessProviderId[]): Observable<MarketplaceItemExtended[] | undefined> {
    return this._mapArray(this.marketplaceQuery.selectMarketplaceItemsOfType(...types));
  }

  /** Favourite marketplace items. */
  selectExtendedMarketplaceFavourites(): Observable<MarketplaceItemExtended[] | undefined> {
    return this._mapArray(this.marketplaceQuery.selectFavourites());
  }

  /**
   * Get the localised attributes for a marketplace item to inputed locale
   * @param marketplaceItemId Id of the marketplace item
   * @param lcid locale to be localised to
   */
  getLocalisedAttributes(marketplaceItemId: number, lcid: number): MarketplaceItemAttribute[] | undefined {
    let marketplaceItem = this.marketplaceQuery.getEntity(marketplaceItemId);

    if (!marketplaceItem) {
      return;
    }

    let attributesToLocalise = JSON.parse(JSON.stringify(marketplaceItem.Attributes)) as MarketplaceItemAttribute[];

    for (let index = 0; index < attributesToLocalise.length; index++) {
      let attribute = attributesToLocalise[index];
      attribute.DisplayName = this.getLocalaisedAttributeName(attribute, lcid);

      for (let avIndex = 0; avIndex < attribute.Values.length; avIndex++) {
        let indexAttributeValue = attribute.Values[avIndex];

        indexAttributeValue.DisplayName = this.getLocalisedValueName(indexAttributeValue, lcid);
        attributesToLocalise[index].Values[avIndex] = indexAttributeValue;
      }
    }
    return attributesToLocalise;
  }

  /**
   * Get the selected attributes from an array of values which the user has selected
   * @param marketplaceItemId Id of the marketplace item
   * @param lcid locale to be localised to
   * @param values selected values which we need to filter the marketplace attributes for
   */
  getSelectedAttributes(marketplaceItemId: number, lcid: number, values: number[]): AttributeValue[] | undefined {
    let marketplaceItem = this.marketplaceQuery.getEntity(marketplaceItemId);

    if (marketplaceItem === undefined) {
      return;
    }

    // to fix error for marketplaceItem being undefined even though it's not possible..
    const foundMarketplaceItem = marketplaceItem;

    let selectedAttributes: AttributeValue[] = [];
    values.forEach((value) => {
      for (let attr of foundMarketplaceItem.Attributes) {
        let foundValue = attr.Values.find((x) => x.RewardValueId === value);
        if (foundValue) {
          let attribute: AttributeValue = {
            AttributeName: this.getLocalaisedAttributeName(attr, lcid),
            ValueName: this.getLocalisedValueName(foundValue, lcid),
            RewardValueId: foundValue.RewardValueId,
          };
          selectedAttributes.push(attribute);
          break;
        }
      }
    });

    return selectedAttributes;
  }

  /**
   * Retrieve the localised attribute values from a list of attributes
   * @param attributes Array of attributes
   * @param lcid locale to be localised to
   */
  getLocalisedAttributeValuesFromArray(attributes: MarketplaceItemAttribute[], lcid: number): AttributeValue[] {
    let selectedAttributes: AttributeValue[] = [];
    for (let attr of attributes) {
      if (attr.Values && attr.Values.length) {
        let attribute: AttributeValue = {
          AttributeName: this.getLocalaisedAttributeName(attr, lcid),
          ValueName: this.getLocalisedValueName(attr.Values[0], lcid),
          RewardValueId: attr.Values[0].RewardValueId,
        };
        selectedAttributes.push(attribute);
      }
    }
    return selectedAttributes;
  }

  /**
   * Retrieve the localised attribute display name for supplied attribute
   * @param attribute Attribute we want to get the localised display name for
   * @param lcid Locale we want to localise the display name to
   */
  private getLocalaisedAttributeName(attribute: MarketplaceItemAttribute, lcid: number): string {
    if (!attribute.Locales) {
      return attribute.DisplayName;
    }

    let matchedAttributeLocale = attribute.Locales.find((l) => l.LCID === lcid);

    if (matchedAttributeLocale) {
      return matchedAttributeLocale.DisplayName;
    } else if (!attribute.DisplayName || attribute.DisplayName === '') {
      return attribute.Locales[0].DisplayName;
    } else {
      return attribute.DisplayName;
    }
  }

  /**
   * Retrieve the localised attribute value display name for supplied attribute value
   * @param attributeValue Attribute value we want to get the localised display name for
   * @param lcid Locale we want to localise the display name to
   */
  private getLocalisedValueName(attributeValue: MarketplaceItemAttributeValue, lcid: number): string {
    if (!attributeValue.Locales) {
      return attributeValue.DisplayName;
    }

    let matchedAttributeValueLocale = attributeValue.Locales.find((l) => l.LCID === lcid);
    if (matchedAttributeValueLocale) {
      return matchedAttributeValueLocale.DisplayName;
    } else if (!attributeValue.DisplayName || attributeValue.DisplayName === '') {
      return attributeValue.Locales[0].DisplayName;
    } else {
      return attributeValue.DisplayName;
    }
  }

  /**
   * Gets the item type and corresponding item group of the marketplace item
   * @param marketplaceItem Marketplace item that we want to identify
   */
  getMarketplaceItemType(marketplaceItem: MarketplaceItemExtended): { backLink: string; type: string; group: string } {
    switch (marketplaceItem.ProcessProviderId) {
      case ProcessProviderId.Event:
      case ProcessProviderId.VirtualEvent:
        return {
          backLink: '/rewards/events',
          type: 'Event',
          group: 'Events',
        };
      case ProcessProviderId.Product:
        return {
          backLink: '/rewards/products',
          type: 'Product',
          group: 'Products',
        };
      default:
        return {
          backLink: '/rewards/marketplace',
          type: 'Marketplace',
          group: 'Marketplace',
        };
    }
  }

  private _mapArray(
    marketplaceItems: Observable<MarketplaceItem[] | undefined>
  ): Observable<MarketplaceItemExtended[] | undefined> {
    return combineQueries([marketplaceItems, this.balanceQuery.selectPurse(config.purseConfig.virtualLoyalty)]).pipe(
      map(([items, balance]) => {
        if (!items || balance === undefined) {
          return;
        }

        return items.map((item) => this._transform(item, balance));
      })
    );
  }

  private _mapSingle(marketplaceItems: Observable<MarketplaceItem | undefined>): Observable<MarketplaceItemExtended | undefined> {
    return combineQueries([marketplaceItems, this.balanceQuery.selectPurse(config.purseConfig.virtualLoyalty)]).pipe(
      map(([item, balance]) => {
        if (!item || balance === undefined) {
          return;
        }
        return this._transform(item, balance);
      })
    );
  }

  private _transform(item: MarketplaceItem, balance: number): MarketplaceItemExtended {
    return {
      ...item,
      Status: this._calculateItemStatus(item, balance),
    } as MarketplaceItemExtended;
  }
  /**
   * Calculates the status of a marketplace item. An item can fulfil all the criteria for multiple
   * statuses simultaneously, however only the highest priority status is returned.
   * @param marketplaceItem The marketplace item to calculate the status for.
   */
  private _calculateItemStatus(marketplaceItem: MarketplaceItem, loyaltyBalance: number): MarketplaceItemStatus {
    let isLimited = !!marketplaceItem.ItemLimitType || !!marketplaceItem.LimitType;

    const requestTime = DateTime.fromISO(marketplaceItem.RequestTime as string, { zone: 'utc' });
    const startDate = DateTime.fromISO(marketplaceItem.ItemStartDate as string, { zone: 'utc' });

    if (marketplaceItem.ComingSoon && startDate > requestTime) {
      return MarketplaceItemStatus.ComingSoon;
    } else if (marketplaceItem.AvailabilityCount <= 0 && !marketplaceItem.UnlimitedStock) {
      return MarketplaceItemStatus.SoldOut;
    } else if (marketplaceItem.PointValue > loyaltyBalance) {
      return MarketplaceItemStatus.NotEnoughPoints;
    } else if (isLimited && marketplaceItem.MaxQuantity == 0) {
      return MarketplaceItemStatus.MemberLimitReached;
    } else {
      return MarketplaceItemStatus.Available;
    }
  }

  /**
   * Check the attributes items ordinals and order attributesMapping array itme according to these
   * attributes items ordinals.
   * @param marketplaceItem the MarketplaceItem item to sort attributes for
   */
  sortAttributeMappingsByValueOrdinals(
    marketplaceItem: MarketplaceItemExtended | undefined
  ): MarketplaceItemExtended | undefined {
    if (!marketplaceItem?.Attributes?.length || !marketplaceItem?.AttributeMappings?.length) return marketplaceItem;

    // explicitly type the map to use number keys
    const valueOrdinalMap: Record<number, number> = {};

    marketplaceItem.Attributes.forEach((attr) => {
      attr.Values?.forEach((val) => {
        valueOrdinalMap[val.RewardValueId] = val.Ordinal ?? 0;
      });
    });

    const sortedMappings = [...marketplaceItem.AttributeMappings].sort((a, b) => {
      const aOrdinals = a.AttributeValues.map((id) => valueOrdinalMap[id] ?? 0);
      const bOrdinals = b.AttributeValues.map((id) => valueOrdinalMap[id] ?? 0);

      const len = Math.max(aOrdinals.length, bOrdinals.length);
      for (let i = 0; i < len; i++) {
        const diff = (aOrdinals[i] ?? 0) - (bOrdinals[i] ?? 0);
        if (diff !== 0) return diff;
      }
      return 0;
    });

    return { ...marketplaceItem, AttributeMappings: sortedMappings };
  }
  setAttributeDisplayNameByLocale<T extends MarketplaceItemExtended | MarketplaceItem>(
    marketplaceItem: T | undefined,
    lcid: number
  ): T | undefined {
    if (!marketplaceItem?.Attributes?.length || !marketplaceItem?.AttributeMappings?.length) return marketplaceItem;

    let attributesToLocalise = JSON.parse(JSON.stringify(marketplaceItem.Attributes)) as MarketplaceItemAttribute[];

    for (let index = 0; index < attributesToLocalise.length; index++) {
      let attribute = attributesToLocalise[index];
      attribute.DisplayName = this.getLocalaisedAttributeName(attribute, lcid);

      for (let avIndex = 0; avIndex < attribute.Values.length; avIndex++) {
        let indexAttributeValue = attribute.Values[avIndex];

        indexAttributeValue.DisplayName = this.getLocalisedValueName(indexAttributeValue, lcid);
        attributesToLocalise[index].Values[avIndex] = indexAttributeValue;
      }
    }

    return { ...marketplaceItem, Attributes: attributesToLocalise } as T;
  }
}
